import { gaInternalFetch } from "@utils/httpClientCore";
import { assert } from "@utils/assertion";

export const CSRF_TOKEN_HIDDEN_INPUT_ID = "_tk";

export async function getCsrfToken(): Promise<string> {
  /* eslint-disable-next-line */
  const csrfToken = (globalThis as any).gaCsrfToken ?? "";
  if (csrfToken === "") {
    return await getAndUpdateCsrfToken();
  } else {
    return csrfToken;
  }
}

function updateCsrfToken(token: string): void {
  /* eslint-disable-next-line */
  (globalThis as any).gaCsrfToken = token;
}

/*
 * Function to install an interval timer that performs an ajax call on every tick,
 * thus keeping the user's HTTP session alive as long as the browser window is open.
 * That allows for a shorter session timeout time on the server, which saves resources.
 * The procedure stops either after options.maxCount cycles or as soon as the server responds with an empty response (instead of a valid CSRF token).
 * Options:
 *  - url: the URL for the ajax call (default: /session-keepalive)
 *  - interval: duration in ms between ajax calls (default: 540000 = 9min)
 *  - maxCount: stop the keep alive after n ticks. maxCount * interval effectively gives a max session timeout. (Default: 160)
 */
type KeepaliveOptions = {
  url?: string;
  interval?: number;
  maxCount?: number;
};

export async function getAndUpdateCsrfToken(): Promise<string> {
  // Even if the session timed out in the meantime, this helps us to at least recover the sessions UUID for tracking reasons
  /* eslint-disable-next-line */
  const sessionUuid = (globalThis as any).gaSessionUuid;

  // The repeating function
  const response = await gaInternalFetch("/session-keepalive", {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(sessionUuid != null ? { uuid: sessionUuid } : undefined),
    },
  });
  const csrfToken = await response.json();
  assert(typeof csrfToken === "string");
  if (csrfToken !== "") {
    updateCsrfToken(csrfToken);
  }
  return csrfToken;
}

export function setupSessionKeepalive(): void {
  // Init variables
  let handle: number | undefined = undefined;
  let counter = 0;
  /* eslint-disable-next-line */
  const customOptionsString = (globalThis as any).gaSessionUuidOptions ?? null;
  // eslint-disable-next-line no-restricted-syntax
  const customOptions = customOptionsString !== null ? (JSON.parse(customOptionsString) as KeepaliveOptions) : {};
  const settings = {
    ...{
      interval: 540000, // 9min (session timeout is 10min)
      maxCount: 160, // 160 * 9min = 24h
    },
    ...customOptions,
  };

  // The repeating function
  const keepAlive = async function (): Promise<void> {
    const csrfToken = await getAndUpdateCsrfToken();
    if (csrfToken === "" || ++counter >= settings.maxCount) {
      // Let the server stop the loop with an empty response body, or after 100x10min = 16h
      window.clearInterval(handle);
    }
  };

  // Start the keep alive timer
  handle = window.setInterval(keepAlive, settings.interval);
}
